import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import BaziChart from "../../../custom/bazi-chart/bazi-chart";
import ConfirmDialog from "../../../dialog/confirm/confirm-dialog";
import useAuth from "../../../../hooks/useAuth";
import { useLoginGate } from "../../../../context/login-gate";
import {
  chartFromUser,
  missingChartFields,
} from "../../../../utils/birth-input";
import "./baiye-chat-style.scss";

//白夜的頭像。放在 public/ 而不是 import 進來：
//  角色圖是內容而不是程式資產，換圖時直接覆蓋檔案就好，不用重新編譯。
//
//  ⚠️ 這裡原本寫死成 fuyaozi.jpg —— 問卦與提問共用同一支元件時，
//  頭像沒跟著 persona 走，所以白夜的提問頁一直顯示伏爻子的臉。
//  拆成兩包之後各自指自己的圖，這個 bug 自然消失。
const MASTER_AVATAR = {
  backgroundImage: `url(${process.env.PUBLIC_URL}/images/characters/baiye.jpg)`,
};

const MASTER = "白夜";

//輸入框長到這個高度就不再長，改成自己捲（約五行）。
//  不設上限的話，貼一大段進來會把整個聊天室頂掉。
const MAX_INPUT_HEIGHT = 120;

//提問 — 聊天（框架）
//
//  接待的是白夜（八字批命）。他答的是「就著你的命盤問」——
//  所以空狀態引導的方向跟問卦相反：問卦要你把問題問窄（一卦一事），
//  提問則是對著你自己的命盤問，範圍本來就寬。
//
//  目前只有外框與送出流程，沒有實際對話內容 ——
//  訊息之後由 API 取得與送出。
//
//  訊息的形狀（接後端時照這個給）：
//    { id, role: "user" | "master", name?, text, blocks? }
//  name 沒給就用 MASTER —— 單則訊息可以自己指定發話者
//  blocks 是老師訊息可夾帶的結構化內容，跟著訊息一起下來而不是另外查表：
//    { type: "chart", data: { title, note, columns[] } }   → BaziChart
//  這樣一則訊息就是完整的一份資料，不用在前端拼湊。
const BaiyeChat = ({
  messages = [],
  onSend,
  error = "",
  ready = true,
  hasMore = false,
  loadingMore = false,
  onLoadMore,
  remainCount = 0,
  onNeedTicket,
}) => {
  const { user, isLoggedIn } = useAuth();
  const { requireLogin } = useLoginGate();
  const navigate = useNavigate();
  const [draft, setDraft] = useState("");
  //生辰還缺哪幾項。空陣列代表齊了；有東西就是要擋下來
  const [missing, setMissing] = useState([]);
  const bottomRef = useRef(null);
  const inputRef = useRef(null);

  //—— 輸入框跟著內容長高 ——
  //
  //  Enter 是斷行不是送出（Kevin 2026-10-08），所以這裡是 textarea 而不是
  //  input。textarea 的高度不會自己跟著內容走，要自己量：
  //  先把高度歸零才量得到真正的 scrollHeight（不歸零的話它只會變高不會變矮，
  //  刪字之後框會一直卡在最高的那個高度）。
  //
  //  寫在 effect 裡而不是 onChange：送出之後是用 setDraft("") 清掉的，
  //  那一條路徑不經過 onChange，掛在那裡會讓框停在三行高。
  useEffect(() => {
    const el = inputRef.current;
    if (!el) return undefined;

    const fit = () => {
      el.style.height = "auto";
      const want = el.scrollHeight;
      el.style.height = `${Math.min(want, MAX_INPUT_HEIGHT)}px`;
      //  —— 只有真的裝不下才開捲軸 ——
      //
      //  一直掛著 overflow-y: auto 的話，內容只要比框高一兩個 px
      //  就會冒出一條捲軸（macOS 是一截白色的拇指壓在框的右緣，
      //  Kevin 2026-10-12 回報的「不明的白色標記」就是它）。
      //  高度本來就是照內容算的，沒到上限時根本不該能捲。
      el.style.overflowY = want > MAX_INPUT_HEIGHT ? "auto" : "hidden";
    };

    fit();

    //  —— 字型換好之後要再量一次 ——
    //
    //  正文是 Google Fonts 的 Noto Serif TC，載完才會換上去。
    //  換字型會讓同一行變高一兩個 px，而這支 effect 的依賴只有 draft ——
    //  不補量的話那一兩個 px 就一直溢出，捲軸會一直掛在那裡。
    let alive = true;
    document.fonts?.ready?.then(() => {
      if (alive) fit();
    });
    return () => {
      alive = false;
    };
  }, [draft]);

  const topRef = useRef(null);
  const listRef = useRef(null);
  //  往回載入之前的頁面總高度。有值代表「這一次 messages 變動是往前接的」。
  const anchorRef = useRef(null);
  //  上一次看到的第一則與最後一則是誰。用來分辨「往回接了一段」與「有新訊息」。
  const firstIdRef = useRef(null);
  const lastIdRef = useRef(null);

  //—— 捲動：新訊息捲到底，往回載入則留在原地 ——
  //
  //  這兩件事一定要分開。一律捲到底的話，往回翻一頁就會被彈回最新那一題，
  //  等於翻不動；一律不捲的話，白夜的回答會掉在畫面外。
  //
  //  用 useLayoutEffect 而不是 useEffect：要在瀏覽器畫出來之前就把捲軸補回去，
  //  不然會先看到畫面往上跳一大段再被拉回來。
  useLayoutEffect(() => {
    const firstId = messages[0]?.id ?? null;
    const lastId = messages[messages.length - 1]?.id ?? null;

    //  往回接了一段 → **第一則**換人了
    //  有新訊息     → **最後一則**換人了（回答回來時 id 會從 pending-xxx
    //                 變成 xxx，那也算 —— 那一刻正好是回答落地）
    //
    //  ⚠️ 不要只看 anchorRef 有沒有值就判定是往回接：哨兵一進畫面就會把它
    //  設好，但 onLoadMore 可能直接早退（沒有游標、或正在載入中），
    //  那個值就留在那裡沒人清，下一次有新訊息時會走錯分支。
    const anchor = anchorRef.current;
    const prepended = anchor != null && firstId !== firstIdRef.current;
    const appended = Boolean(lastId) && lastId !== lastIdRef.current;

    //  不管這次是哪一種，anchor 都要清掉 —— 它只對緊接著的那一次有效
    anchorRef.current = null;
    firstIdRef.current = firstId;
    lastIdRef.current = lastId;

    //  往回接了一段：頁面變高多少，捲軸就往下補多少，視線停在原來那一則上
    if (prepended) {
      const delta = document.documentElement.scrollHeight - anchor;
      if (delta) window.scrollBy(0, delta);
      return;
    }

    //  空的時候不捲：否則一進頁面就把空狀態推出畫面
    if (appended) bottomRef.current?.scrollIntoView({ block: "end" });
  }, [messages]);

  //—— 捲到最上面就往回翻一頁 ——
  //
  //  用 IntersectionObserver 盯一個哨兵元素，不自己聽 scroll 事件 ——
  //  scroll 在行動裝置上一秒會觸發幾十次，每次都要讀 scrollTop 造成重排。
  //
  //  rootMargin 給 240px：讓它在哨兵還沒真的進畫面時就先去要資料，
  //  等捲到頂那一段通常已經接上了。
  useEffect(() => {
    const el = topRef.current;
    if (!el || !hasMore || loadingMore) return undefined;

    const io = new IntersectionObserver(
      (entries) => {
        if (!entries[0].isIntersecting) return;
        //  在發請求的這一刻記下高度。從這裡到資料接上為止，畫面上唯一
        //  會變高的就是接進來的那一段 —— 載入中的那一列是固定高度的
        //  （見 __more），不然這個差值就不準。
        anchorRef.current = document.documentElement.scrollHeight;
        onLoadMore?.();
      },
      { rootMargin: "240px 0px 0px 0px" },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [hasMore, loadingMore, onLoadMore]);

  const handleSend = (e) => {
    e.preventDefault();
    const text = draft.trim();
    if (!text) return;

    //  再擋一次。focus 那一道是體驗（讓他在打字之前就知道要登入），
    //  這一道是保險 —— 外接鍵盤、自動填入、瀏覽器還原草稿都可能繞過 focus。
    if (!isLoggedIn) {
      requireLogin(null, "登入後才能保留你的提問紀錄，日後隨時回來看。");
      return;
    }

    //  生辰不齊也擋。跟登入同理：focus 那一道是體驗，這一道是保險 ——
    //  他可能在別的分頁把資料清掉了，或根本沒經過 focus（貼上、自動填入）。
    const lack = missingChartFields(chartFromUser(user));
    if (lack.length > 0) {
      setMissing(lack);
      return;
    }

    //沒有問事券就不送出，改為引導購買 ——
    //讓人打完字才發現不能問是很糟的體驗，所以送出前就攔下來
    if (remainCount <= 0) {
      onNeedTicket?.();
      return;
    }

    //  真正的扣券、排盤與作答都在頁面那一層（它才知道餘額、
    //  也才持有整串提問）。這裡只負責把字交出去。
    onSend?.(text);
    setDraft("");
  };

  //—— 登入的攔截點就在這裡 ——
  //
  //  這一頁刻意不擋在門口（見 App.js 的路由說明）：先讓他進來看到角色、
  //  看到提問長什麼樣，等他真的要打字才跳登入 —— 那時候他已經想問了，
  //  才有理由答應。
  //
  //  手機上 focus 會把鍵盤推上來，而登入彈窗是置中的 —— 不先收鍵盤的話
  //  彈窗會被蓋掉一半。所以 blur 再跳。
  const handleFocus = (e) => {
    if (!isLoggedIn) {
      e.currentTarget.blur();
      //  不帶 to：登入完成後留在這一頁繼續打字，不要把他導去別的地方
      requireLogin(null, "登入後才能保留你的提問紀錄，日後隨時回來看。");
      return;
    }

    //—— 第二道：生辰齊了沒 ——
    //
    //  白夜答的是「就著你的命盤問」，沒有命盤就無從答起。
    //  擋在打字之前而不是送出之後 —— 打完一整段問題才被退回去填生日，
    //  那段字還會不見。
    //
    //  同樣要先 blur：手機上鍵盤已經推上來了，而 dialog 是置中的，
    //  不收鍵盤的話會被蓋掉一半（跟登入彈窗同一個理由）。
    const lack = missingChartFields(chartFromUser(user));
    if (lack.length > 0) {
      e.currentTarget.blur();
      setMissing(lack);
    }
  };

  return (
    <div className="baiye-chat">
      {/* 歷史還沒拉回來之前兩邊都不畫。
          畫空狀態的話，問過二十題的人會先看到「就著你的命盤問」
          再被歷史蓋掉，閃一下很糟。 */}
      {!ready ? null : messages.length === 0 ? (
        <div className="baiye-chat__empty">
          <span
            className="baiye-chat__empty-avatar"
            style={MASTER_AVATAR}
            aria-hidden="true"
          />
          <h2 className="baiye-chat__empty-title">就著你的命盤問</h2>
          <p className="baiye-chat__empty-desc">
            白夜看的是你這塊材料本身。
            <br />
            「我適合創業嗎」勝過「我明天該不該出門」。
          </p>
        </div>
      ) : (
        <div className="baiye-chat__list" ref={listRef}>
          {/* 往回翻的哨兵。高度固定 —— 它會在「載入中」與「空的」之間切換，
              高度一變就會把捲軸要補回去的差值算歪。 */}
          {hasMore && (
            <div className="baiye-chat__more" ref={topRef}>
              {loadingMore ? "正在翻出更早的提問…" : ""}
            </div>
          )}

          {messages.map((msg) =>
            msg.role === "user" ? (
              <div
                className="baiye-chat__row baiye-chat__row--user"
                key={msg.id}
              >
                <p className="baiye-chat__bubble">{msg.text}</p>
              </div>
            ) : (
              <div className="baiye-chat__row" key={msg.id}>
                <div className="baiye-chat__master">
                  <span
                    className="baiye-chat__avatar"
                    style={MASTER_AVATAR}
                    aria-hidden="true"
                  />
                  <span className="baiye-chat__name">{msg.name || MASTER}</span>
                </div>

                {/* 白夜的回覆整包在一個框裡（同問卦的 __reply）——
                    有個框才看得出「這是一則完整的回覆」而不是一坨字。 */}
                <div
                  className={`baiye-chat__reply${msg.typing ? " is-typing" : ""}`}
                >
                  {msg.typing ? (
                    //  正在作答。三個點而不是一句「容我細看片刻」——
                    //  這是一問一答的對話，對話裡「對方正在輸入」就是三個點；
                    //  講一句話反而像他已經回答了，而那不是回答。
                    <span
                      className="baiye-chat__typing"
                      role="status"
                      aria-label="白夜正在回覆"
                    >
                      <i aria-hidden="true" />
                      <i aria-hidden="true" />
                      <i aria-hidden="true" />
                    </span>
                  ) : (
                    //  回答可能自己帶空行分段。split 之後逐段畫 ——
                    //  直接塞一整串的話換行會被 HTML 吃掉。
                    String(msg.text || "")
                      .split(/\n\s*\n/)
                      .filter(Boolean)
                      .map((para, k) => (
                        <p className="baiye-chat__text" key={k}>
                          {para.trim()}
                        </p>
                      ))
                  )}

                  {/* 老師訊息可夾帶命盤之類的結構化內容 */}
                  {msg.blocks?.map((block, i) =>
                    block.type === "chart" ? (
                      <BaziChart key={i} data={block.data} />
                    ) : null,
                  )}
                </div>
              </div>
            ),
          )}
          <div ref={bottomRef} />
        </div>
      )}

      {/* 送出失敗的原因。擺在輸入列上方而不是用 alert ——
          使用者要能一邊看著原因一邊改自己的問題。 */}
      {error && <p className="baiye-chat__error">{error}</p>}

      {/* 輸入列固定在底部 */}
      <form className="baiye-chat__composer" onSubmit={handleSend}>
        {/* 顯示是「誰」在問：合盤等情境下可能替別人提問 */}
        <span className="baiye-chat__who">{user?.userName || "我"}</span>

        {/* textarea 而不是 input：Enter 要能斷行。
            input 在 form 裡按 Enter 會直接送出，那不是他要的 ——
            問題常常要分兩三句講清楚。送出只走右邊那顆鈕。 */}
        <textarea
          ref={inputRef}
          className="baiye-chat__input"
          rows={1}
          onFocus={handleFocus}
          placeholder="我適合創業嗎？"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          aria-label="輸入問題"
        />

        <button
          type="submit"
          className="baiye-chat__send"
          //沒打字就不能送：按了沒反應會讓人以為壞了
          disabled={!draft.trim()}
          aria-label="送出"
        >
          {/* 紙飛機。實心而不是描邊 —— 這顆鈕是金底深色字，
              2px 的線在 36px 的圓裡會顯得虛，實心才撐得住。 */}
          <svg viewBox="0 0 24 24" width="19" height="19" aria-hidden="true">
            <path
              d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"
              fill="currentColor"
            />
          </svg>
        </button>
      </form>

      {/* 生辰不齊。純告知 + 去填 —— 不給「下次再說」那種選項，
          因為留在這一頁也什麼都問不了。點遮罩或取消就是留在原地。

          確認後導去個人資料頁；那一頁存完會自己 navigate(-1) 回到這裡
          （見 personal-editor-page），所以不用把回程寫在這裡。 */}
      <ConfirmDialog
        open={missing.length > 0}
        title="先讓白夜看一眼你的盤"
        message={`他批的是你這塊材料本身，少了${missing.join("、")}就無從看起。現在去補，補完回來就能問。`}
        confirmTxt="去填寫"
        cancelTxt="稍後"
        onSuccess={() => {
          setMissing([]);
          navigate("/personal-editor");
        }}
        onClose={() => setMissing([])}
      />
    </div>
  );
};

export default BaiyeChat;
